
import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Attributes,
  Condition,
  Creature,
  CreatureGift,
  SENSES,
  Skill,
  SKILLS,
  Tool,
} from "@dndModels";
import { 
  DAMAGE_TYPES,
  PROFICIENCY_OPTIONS
} from "@dndModels/Constants";
import { AbilityScoreType, DamageType } from "@dndModels/Types";

import DandDAPI from "@dndUtilities/system_api";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import LoadingBox from "@components/input/LoadingBox";

interface Props {
  attributes: Attributes;
  disabled: boolean;
  onChange: () => void;
}

const api = DandDAPI.getInstance();

export default function AttributesInput({
  attributes,
  disabled,
  onChange
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [edit_attribute, setEditAttribute] = useState("");
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);

  const load = useCallback( async () => {
    setLoading(true);
    const res = await api.getSetOfObjects({ data_types: ["condition","tool"] });
    if (res) {
      setConditions(res.condition as Condition[]);
      setTools(res.tool as Tool[]);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if(reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderSpeeds = (use_entry: Attributes) => {
    const return_me: any[] = [];
    return_me.push(renderSpeed(use_entry, "walk", "Walk (in feet)"));
    return_me.push(renderSpeed(use_entry, "swim", "Swim (in feet)"));
    return_me.push(renderSpeed(use_entry, "climb", "Climb (in feet)"));
    return_me.push(renderSpeed(use_entry, "fly", "Fly (in feet)"));
    return_me.push(renderSpeed(use_entry, "burrow", "Burrow (in feet)"));
    return_me.push(  
      <Grid
        key="hover"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name="Hover"
          value={ use_entry.hover }
          disabled={disabled}
          onToggle={() => {
            // const new_obj = obj;
            use_entry.hover = !use_entry.hover;
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
    return return_me;
  }

  const renderSpeed = (use_entry: Attributes, name: string, display: string) => {
    const speed = use_entry.speeds[name];
    return (
      <Grid
        key={name}
        size={{
          xs: 4
        }}>
        <StringBox
          name={ display }
          value={ speed && speed > 0 ? `${speed}` : "" }
          type="number"
          disabled={disabled}
          onBlur={(changed: string) => {
            // const new_obj = obj;
            if (changed === "") {
              use_entry.speeds[name] = 0;
            } else {
              use_entry.speeds[name] = +changed;
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderSenses = (use_entry: Attributes) => {
    const return_me: any[] = [];
    for (const sense of SENSES) {
      return_me.push(renderSense(use_entry, sense.name));
    }
    return return_me;
  }

  const renderSense = (use_entry: Attributes, name: string) => {
    const range = use_entry.senses[name];
    return (
      <Grid
        key={name}
        size={{
          xs: 4
        }}>
        <StringBox
          name={ `${name} (in feet)` }
          value={ range ? `${range}` : "" }
          type="number"
          disabled={disabled}
          onBlur={(changed: string) => {
            // const new_obj = obj;
            if (changed === "" || changed === "0") {
              delete use_entry.senses[name];
            } else {
              use_entry.senses[name] = +changed;
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderSkill = (use_entry: Attributes, skill: Skill) => {
    // if (skill.name === "Arcana") {
    //   // console.log(skill);
    //   // console.log(obj.skill_proficiencies2[skill._id]);
    // }
    return (
      <Grid
        key={skill._id}
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name={ skill.name }
          value={ use_entry.skill_profs[skill._id] ? `${use_entry.skill_profs[skill._id]}` : "0" }
          options={PROFICIENCY_OPTIONS}
          disabled={disabled}
          onChange={(changed: string) => {
            // const new_obj = obj;
            if (changed === "0") {
              delete use_entry.skill_profs[skill._id];
            } else {
              use_entry.skill_profs[skill._id] = +changed;
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderSavingThrow = (use_entry: Attributes, ability_score: AbilityScoreType) => {
    return (
      <Grid
        size={{
          xs: 4
        }}>
        <CheckBox
          name={ ability_score }
          value={ use_entry.save_profs.includes(ability_score) }
          disabled={disabled}
          onChange={(changed: boolean) => {
            // const new_obj = obj;
            if (changed) {
              use_entry.save_profs.push(ability_score);
            } else {
              use_entry.save_profs = use_entry.save_profs.filter(o => o !== ability_score);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderTool = (use_entry: Attributes, tool: Tool) => {
    return (
      <Grid
        key={tool._id}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ tool.name }
          disabled={disabled}
          value={ use_entry.tool_profs.includes(tool.name) }
          onToggle={() => {
            // const new_obj = obj;
            if (use_entry.tool_profs.includes(tool.name)) {
              use_entry.tool_profs = use_entry.tool_profs.filter(o => o !== tool.name);
            } else {
              use_entry.tool_profs.push(tool.name);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderCondition = (use_entry: Attributes, condition: Condition) => {
    return (
      <Grid
        key={condition._id}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ condition.name }
          disabled={disabled}
          value={ use_entry.condition_immunities.includes(condition.name) }
          onToggle={() => {
            // const new_obj = obj;
            if (use_entry.condition_immunities.includes(condition.name)) {
              use_entry.condition_immunities = use_entry.condition_immunities.filter(o => o !== condition.name);
            } else {
              use_entry.condition_immunities.push(condition.name);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderDamageType = (use_entry: Attributes, damage_type: DamageType, multiplier: number) => {
    const damage_multipliers = multiplier === 0 ? use_entry.damage_immunities : (multiplier === 0.5 ? use_entry.damage_resistances : use_entry.damage_vulnerabilities);
    return (
      <Grid
        key={damage_type}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ damage_type }
          disabled={disabled}
          value={ damage_multipliers.includes(damage_type) }
          onToggle={() => {
            // const new_obj = obj;
            let damage_types = multiplier === 0 ? use_entry.damage_immunities : (multiplier === 0.5 ? use_entry.damage_resistances : use_entry.damage_vulnerabilities);
            if (damage_types.includes(damage_type)) {
              damage_types = damage_types.filter(o => o !== damage_type);
            } else {
              damage_types.push(damage_type);
            }
            if (multiplier === 0) {
              use_entry.damage_immunities = damage_types;
            } else if (multiplier === 0.5) {
              use_entry.damage_resistances = damage_types;
            } else {
              use_entry.damage_vulnerabilities = damage_types;
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    if (edit_attribute === "speed") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Speed
          </Grid>
          { renderSpeeds(attributes) }
        </Grid>
      );
    } else if (edit_attribute === "senses") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Senses
          </Grid>
          { renderSenses(attributes) }
        </Grid>
      );
    } else if (edit_attribute === "condition_immunities") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Condition Immunities
          </Grid>
          { conditions && conditions.filter(o => o.common).map(condition => {
            return renderCondition(attributes, condition);
          })}
        </Grid>
      );
    } else if (edit_attribute === "immunities") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Damage Immunities
          </Grid>
          { DAMAGE_TYPES.map(damage_type => {
            return renderDamageType(attributes, damage_type, 0);
          })}
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Extra Damage Immunities"
              value={ attributes.extra_damage_immunities }
              disabled={disabled}
              onBlur={(changed: string) => {
                // const new_obj = obj;
                attributes.extra_damage_immunities = changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
        </Grid>
      );
    } else if (edit_attribute === "resistances") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Damage Resistances
          </Grid>
          { DAMAGE_TYPES.map(damage_type => {
            return renderDamageType(attributes, damage_type, 0.5);
          })}
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Extra Damage Resistances"
              value={ attributes.extra_damage_resistances }
              disabled={disabled}
              onBlur={(changed: string) => {
                // const new_obj = obj;
                attributes.extra_damage_resistances = changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
        </Grid>
      );
    } else if (edit_attribute === "vulnerabilities") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Damage Vulnerabilities
          </Grid>
          { DAMAGE_TYPES.map(damage_type => {
            return renderDamageType(attributes, damage_type, 2);
          })}
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Extra Damage Vulnerabilities"
              value={ attributes.extra_damage_vulnerabilities }
              disabled={disabled}
              onBlur={(changed: string) => {
                // const new_obj = obj;
                attributes.extra_damage_vulnerabilities = changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
        </Grid>
      );
    } else if (edit_attribute === "tools") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Tools
          </Grid>
          { tools && tools.map(tool => {
            return renderTool(attributes, tool);
          })}
        </Grid>
      );
    } else if (edit_attribute === "skills") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Skills
          </Grid>
          { SKILLS.sort((a,b) => a.name.localeCompare(b.name)).map(skill => {
            return renderSkill(attributes, skill);
          })}
        </Grid>
      );
    } else if (edit_attribute === "saving_throws") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Saving Throws
          </Grid>
          { renderSavingThrow(attributes, "STR") }
          { renderSavingThrow(attributes, "DEX") }
          { renderSavingThrow(attributes, "CON") }
          { renderSavingThrow(attributes, "INT") }
          { renderSavingThrow(attributes, "WIS") }
          { renderSavingThrow(attributes, "CHA") }
        </Grid>
      );
    } else if (edit_attribute === "languages") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ cursor: "pointer", fontWeight: "bold" }}
            onClick={() => {
              setEditAttribute("");
            }}
            size={{
              xs: 2
            }}>
            Back
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            Languages
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Languages"
              value={attributes.languages}
              disabled={disabled}
              onBlur={(changed: string) => {
                // const new_obj = obj;
                attributes.languages = changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Grid container spacing={0} direction="column" style={{ cursor: "pointer" }}>
          <Grid
            onClick={() => {
              setEditAttribute("speed");
            }}>
            <b>Speed</b> { attributes.speed_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("senses");
            }}>
            <b>Senses</b> { attributes.senses_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("immunities");
            }}>
            <b>Damage Immunities</b> { attributes.immunities_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("resistances");
            }}>
            <b>Damage Resistances</b> { attributes.resistances_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("vulnerabilities");
            }}>
            <b>Damage Vulnerabilities</b> { attributes.vulnerabilities_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("condition_immunities");
            }}>
            <b>Condition Immunities</b> { attributes.condition_immunities_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("tools");
            }}>
            <b>Tools</b> { attributes.tools_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("skills");
            }}>
            <b>Skills</b> { attributes.skills_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("saving_throws");
            }}>
            <b>Saving Throws</b> { attributes.saving_throws_string }
          </Grid>
          <Grid
            onClick={() => {
              setEditAttribute("languages");
            }}>
            <b>Languages</b> { attributes.languages }
          </Grid>
        </Grid>
      );
    }
  }
}
